// Stable per-device identifier for keying FCM push tokens.
//
// Web Push tokens are per-device, so each browser/device the user signs in on
// needs its own key in `staff/{uid}.devices`. We persist a random id in
// localStorage — stable across sign-ins on the same device, distinct across
// devices. Cleared if the user wipes site data (a new id + token is then
// registered, and the stale entry ages out / is toggled off by the user).

import { logger } from '@/core/logger'

const DEVICE_ID_KEY = 'ucm.deviceId'

/**
 * Returns this device's stable id, generating + persisting one on first call.
 * Falls back to an ephemeral id when localStorage is unavailable (private
 * mode) — push still works for the session, it just won't dedupe across
 * reloads, which is acceptable for an opt-in nice-to-have channel.
 */
export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY)
    if (existing) return existing
    const id = crypto.randomUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
    return id
  } catch (err) {
    logger.d('deviceId: localStorage unavailable, using ephemeral id', err)
    return `ephemeral-${crypto.randomUUID()}`
  }
}

/**
 * A short human label for the per-device toggle list, derived from the UA.
 * Best-effort — "Chrome · macOS", "Safari · iOS", else "Este dispositivo".
 */
export function getDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Este dispositivo'
  const ua = navigator.userAgent
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    'Navegador'
  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
    /Android/.test(ua) ? 'Android' :
    /Macintosh/.test(ua) ? 'macOS' :
    /Windows/.test(ua) ? 'Windows' :
    /Linux/.test(ua) ? 'Linux' :
    ''
  return os ? `${browser} · ${os}` : browser
}
